import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns'
import { it } from 'date-fns/locale'
import { CalendarClock } from 'lucide-react'
import { RedAgendaNota } from '../../types/red'
import { agendaApi } from '../../services/api'
import NuovaNotaAgendaModal from '../../components/red/NuovaNotaAgendaModal'

type Vista = 'mese' | 'settimana' | 'lista'

const RedAgenda: React.FC = () => {
  const [vista, setVista] = useState<Vista>('mese')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [note, setNote] = useState<RedAgendaNota[]>([])
  const [loading, setLoading] = useState(true)
  const [showNuova, setShowNuova] = useState(false)
  const [dataPerNuova, setDataPerNuova] = useState<Date | undefined>(undefined)
  const [dettaglioNota, setDettaglioNota] = useState<RedAgendaNota | null>(null)
  const [notaInModifica, setNotaInModifica] = useState<RedAgendaNota | null>(null)

  const load = () => {
    const da = startOfMonth(subMonths(currentMonth, 1)).toISOString()
    const a = endOfMonth(addMonths(currentMonth, 1)).toISOString()
    agendaApi.list({ da, a }).then(res => {
      setNote(res.data)
      setLoading(false)
    }).catch(() => {
      setLoading(false)
      toast.error('Errore caricamento agenda')
    })
  }

  useEffect(load, [currentMonth])

  const handleToggleStato = async (nota: RedAgendaNota) => {
    try {
      await agendaApi.update(nota.id, { stato: nota.stato === 'fatto' ? 'da_fare' : 'fatto' })
      toast.success(nota.stato === 'fatto' ? 'Segnata come da fare' : 'Segnata come fatta')
      load()
      setDettaglioNota(null)
    } catch {
      toast.error('Errore aggiornamento')
    }
  }

  const handleElimina = async (nota: RedAgendaNota) => {
    if (!confirm('Eliminare questa nota?')) return
    try {
      await agendaApi.delete(nota.id)
      toast.success('Nota eliminata')
      load()
      setDettaglioNota(null)
    } catch {
      toast.error('Errore eliminazione')
    }
  }

  const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) })
  const startDow = (startOfMonth(currentMonth).getDay() + 6) % 7
  const noteDelGiorno = (day: Date) => note.filter(n => isSameDay(new Date(n.data_ora), day))
  const noteFuture = [...note]
    .filter(n => new Date(n.data_ora).getTime() >= Date.now() - 24 * 60 * 60 * 1000)
    .sort((a, b) => new Date(a.data_ora).getTime() - new Date(b.data_ora).getTime())

  const coloreNota = (n: RedAgendaNota) => {
    if (n.stato === 'fatto') return 'bg-green-100 text-green-700 line-through'
    if (n.reminder_at) return 'bg-red-50 text-red-600'
    if (n.tipo === 'appuntamento_telefonico') return 'bg-orange-100 text-orange-700'
    return 'bg-blue-100 text-blue-700'
  }

  if (loading) return <div className="p-8 text-gray-500">Caricamento...</div>

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarClock size={22} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Agenda RED</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['mese', 'settimana', 'lista'] as Vista[]).map(v => (
              <button
                key={v}
                onClick={() => setVista(v)}
                className={`px-3 py-1.5 text-sm capitalize ${vista === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {v}
              </button>
            ))}
          </div>
          <button onClick={() => { setDataPerNuova(undefined); setShowNuova(true) }} className="btn-primary text-sm">+ Nuovo</button>
        </div>
      </div>

      {vista === 'mese' && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="text-gray-500 hover:text-gray-700">← Prec</button>
            <div className="font-semibold text-gray-800 capitalize">{format(currentMonth, 'MMMM yyyy', { locale: it })}</div>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="text-gray-500 hover:text-gray-700">Succ →</button>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-7 mb-1 text-xs text-gray-400 text-center">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: startDow }).map((_, i) => <div key={`pad-${i}`} />)}
                {days.map(day => {
                  const eventiGiorno = noteDelGiorno(day)
                  return (
                    <div key={day.toISOString()} className={`min-h-[80px] max-h-[140px] overflow-y-auto p-1 border rounded ${isToday(day) ? 'border-blue-400 bg-blue-50' : 'border-gray-100'}`}>
                      <div className="text-xs text-gray-400 mb-1">{format(day, 'd')}</div>
                      <div className="space-y-0.5">
                        {eventiGiorno.map(n => (
                          <button
                            key={n.id}
                            onClick={() => setDettaglioNota(n)}
                            className={`block w-full text-left text-[11px] px-1 py-0.5 rounded truncate ${coloreNota(n)}`}
                            title={n.titolo}
                          >
                            {n.titolo}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {vista === 'settimana' && (
        <div className="card p-4">
          <p className="text-sm text-gray-500 mb-3">Prossimi 7 giorni</p>
          <div className="overflow-x-auto">
            <div className="grid grid-cols-7 gap-2 min-w-[700px]">
              {Array.from({ length: 7 }).map((_, i) => {
                const day = new Date(Date.now() + i * 86400000)
                const eventiGiorno = noteDelGiorno(day)
                return (
                  <div key={i} className="border border-gray-100 rounded p-2 min-h-[120px]">
                    <div className="text-xs font-medium text-gray-600 mb-1 capitalize">{format(day, 'EEE d/M', { locale: it })}</div>
                    <div className="space-y-1">
                      {eventiGiorno.map(n => (
                        <button
                          key={n.id}
                          onClick={() => setDettaglioNota(n)}
                          className={`block w-full text-left text-[11px] px-1 py-0.5 rounded truncate ${coloreNota(n)}`}
                        >
                          {format(new Date(n.data_ora), 'HH:mm')} {n.titolo}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {vista === 'lista' && (
        <div className="card p-4 space-y-2">
          {noteFuture.length === 0 && <div className="text-center py-10 text-gray-400">Nessuna nota in agenda.</div>}
          {noteFuture.map(n => (
            <button
              key={n.id}
              onClick={() => setDettaglioNota(n)}
              className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 text-left"
            >
              <div>
                <div className={`text-sm font-medium ${n.stato === 'fatto' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{n.titolo}</div>
                <div className="text-xs text-gray-400">{n.immobile_nome} · {format(new Date(n.data_ora), "d MMM yyyy 'alle' HH:mm", { locale: it })}</div>
              </div>
              {n.reminder_at && <span className="text-xs text-blue-500">🔔</span>}
            </button>
          ))}
        </div>
      )}

      {showNuova && (
        <NuovaNotaAgendaModal
          dataIniziale={dataPerNuova}
          onClose={() => setShowNuova(false)}
          onCreated={() => load()}
        />
      )}

      {notaInModifica && (
        <NuovaNotaAgendaModal
          notaEsistente={notaInModifica}
          onClose={() => setNotaInModifica(null)}
          onCreated={() => load()}
        />
      )}

      {dettaglioNota && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDettaglioNota(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">{dettaglioNota.titolo}</h3>
            <p className="text-sm text-gray-500 mb-2">{dettaglioNota.immobile_nome}</p>
            {dettaglioNota.testo && <p className="text-sm text-gray-700 mb-3">{dettaglioNota.testo}</p>}
            <p className="text-xs text-gray-400 mb-1">Data: {format(new Date(dettaglioNota.data_ora), "d MMM yyyy 'alle' HH:mm", { locale: it })}</p>
            {dettaglioNota.reminder_at && (
              <p className="text-xs text-gray-400 mb-1">Reminder: {format(new Date(dettaglioNota.reminder_at), "d MMM yyyy 'alle' HH:mm", { locale: it })} {dettaglioNota.reminder_inviato ? '(inviato)' : ''}</p>
            )}
            <p className="text-xs text-gray-400 mb-4">Inserito da: {dettaglioNota.creato_da_nome}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => handleElimina(dettaglioNota)} className="text-sm text-red-500 hover:text-red-700">Elimina</button>
              <button
                onClick={() => { setNotaInModifica(dettaglioNota); setDettaglioNota(null) }}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Modifica
              </button>
              <button onClick={() => handleToggleStato(dettaglioNota)} className="btn-primary text-sm">
                {dettaglioNota.stato === 'fatto' ? 'Segna da fare' : 'Segna fatto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RedAgenda
